import React, { useState, useEffect } from 'react';
import { Grid, Button, Card, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import axiosInstance from '../../axiosInstance';
import { useDispatch, useSelector } from 'react-redux';
import { assignReportToCard } from '../../store/actions';
import { styled } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import configData from '../../config';  // Assuming configData contains your API server URL
import Snackbar from '@mui/material/Snackbar';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  height: 'auto',
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: '#f9f9f9',
  textAlign: 'center',
}));

const SmallButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontWeight: 'bold',
  fontSize: '12px',
  padding: theme.spacing(0.5, 2),
  minHeight: '30px',
}));

const DashboardReportAllocationPage = ({ item }) => {
  const history = useHistory();
  const [reports, setReports] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const account = useSelector((state) => state.account);
  const dispatch = useDispatch();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        let response;
        if (account.role === 'Owner') {
          response = await axiosInstance.get(configData.API_SERVER + '/user/get_all_reports');
          setReports(response?.data || []);
        } else if (account.role === 'Super Admin' && account.org !== null) {
          response = await axiosInstance.get(configData.API_SERVER + `/user/allocated-reports?org_id=${account.org.id}`);
          setReports(response?.data || []);
        } else {
          const user = await axiosInstance.get(configData.API_SERVER + `/user/get_user_with_email?email=${account?.org_member?.email}`);
          response = await axiosInstance.get(configData.API_SERVER + `/user/allocated-reports?user_id=${user.data.id}`);
          setReports(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching report data", error);
      }
    };
    fetchData();
  }, [account]);

  const concatenatedReports = (item?.children && Array.isArray(item.children))
    ? [...item.children, ...reports]
    : reports;

  const handleAssignmentChange = (cardIndex, type, reportId) => {
    const selectedReport = concatenatedReports.find((report) => report.id === reportId) || 
                          concatenatedReports.flatMap(r => r.children || []).find((child) => child.id === reportId);

    if (selectedReport) {
      setAssignments((prev) => ({
        ...prev,
        [cardIndex]: {
          ...prev[cardIndex],
          [type]: {
            reportId: selectedReport.id,
            embedURL: selectedReport.embedURL  // Store embedURL for rendered/detailed reports
          }
        }
      }));
    }
  };

  const saveAssignments = async () => {
    try {
      const userResponse = await axiosInstance.get(`${configData.API_SERVER}/user/get_user_with_email?email=${account?.org_member?.email}`);
      const email = userResponse.data.email; // Extract email from the response data

      await axiosInstance.post(`https://api.projectivetech.com/user/save-report-assignments/`, 
        { email, assignments });
      setSnackbarMessage('✅ Report assignments saved!');
      setSnackbarOpen(true);
      history.push({
        pathname: '/dashboard1',
        state: { assignments },
      });
    } catch (error) {
      console.error("Error saving assignments", error);
      setSnackbarMessage('❌ Failed to save assignments!');
      setSnackbarOpen(true);
    }
  };

  const renderDropdownItems = (data) => {
    return data.flatMap((report, index) => {
      if (report.children && report.children.length > 0) {
        return [
          <MenuItem disabled key={`parent-${index}`}>
            {report.title || report.name}
          </MenuItem>,
          ...report.children.map((child, childIndex) => (
            <MenuItem key={`child-${index}-${childIndex}`} value={child.id}>
              &nbsp;&nbsp;&nbsp;{child.title || child.name}
            </MenuItem>
          ))
        ];
      } else {
        return (
          <MenuItem key={`report-${index}`} value={report.id}>
            {report.title || report.name}
          </MenuItem>
        );
      }
    });
  };

  return (
    <div>
      <Typography variant="h5" align="center" gutterBottom>
        Assign Reports to Dashboard Slots
      </Typography>
      <Typography variant="body2" align="center" gutterBottom>
        Select a rendered report and a detailed report for each dashboard slot.
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        {/* Large Dashboard Report */}
        <Grid item xs={12}>
          <StyledCard>
            <Typography variant="subtitle1" gutterBottom>
              Dashboard Report (Large)
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Rendered Report</InputLabel>
              <Select
                value={assignments[0]?.rendered?.reportId || ''}
                onChange={(e) => handleAssignmentChange(0, 'rendered', e.target.value)}
                size="small"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {renderDropdownItems(concatenatedReports)}
              </Select>
            </FormControl>

            <FormControl fullWidth style={{ marginTop: '10px' }}>
              <InputLabel>Detailed Report</InputLabel>
              <Select
                value={assignments[0]?.detailed?.reportId || ''}
                onChange={(e) => handleAssignmentChange(0, 'detailed', e.target.value)}
                size="small"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {renderDropdownItems(concatenatedReports)}
              </Select>
            </FormControl>

          </StyledCard>
        </Grid>

        {/* Six Medium Reports */}
        {Array.from({ length: 6 }).map((_, cardIndex) => (
          <Grid item xs={12} sm={6} md={4} key={cardIndex + 1}>
            <StyledCard>
              <Typography variant="subtitle1" gutterBottom>
                Report Slot {cardIndex + 2} (Medium)
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Rendered Report</InputLabel>
                <Select
                  value={assignments[cardIndex + 1]?.rendered?.reportId || ''}
                  onChange={(e) => handleAssignmentChange(cardIndex + 1, 'rendered', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {renderDropdownItems(concatenatedReports)}
                </Select>
              </FormControl>

              <FormControl fullWidth style={{ marginTop: '10px' }}>
                <InputLabel>Detailed Report</InputLabel>
                <Select
                  value={assignments[cardIndex + 1]?.detailed?.reportId || ''}
                  onChange={(e) => handleAssignmentChange(cardIndex + 1, 'detailed', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {renderDropdownItems(concatenatedReports)}
                </Select>
              </FormControl>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Grid container justifyContent="center">
        <Grid item xs={6} sm={4} md={3}>
          <SmallButton
            onClick={saveAssignments}
            variant="contained"
            color="primary"
            fullWidth
          >
            Save Dashboard Reports
          </SmallButton>
        </Grid>
      </Grid>
    </div>
  );
};

export default DashboardReportAllocationPage;
